// ── THE BLANK WHEAT QUOTE, ON PAPER ──────────────────────────────────────────
//
// Kevin, 2026-09-11: "we have no way of printing a quote from blank quote in
// coverage advisor wheat quoter. We need to be able to print."
//
// The farm quote has printed since August through `wheatQuotePages`, which is
// built entirely around a POLICY — prior-year rows, billed liability, the AIP's
// unit structure and elections, a renewal recommendation. A blank quote has
// none of that: it is a prospect, a county, an APH somebody typed or built, and
// a coverage the agent assembled on screen. So it gets its own sheet rather
// than a policy-shaped document with every prior-year cell blank.
//
// ── ONE COMPUTATION, THREE SURFACES ──────────────────────────────────────────
// `blankQuoteCoverage` is the only place a blank-quote figure is computed. The
// screen's "Build one coverage" panel, the all-levels grid under it, and this
// printed sheet all call it with the same configuration, so the page a
// prospect takes home cannot disagree with the screen the agent quoted from.
// This repo has recorded that failure more than once — the card and the sheet
// called `recommendWheat` with different arguments and 175 of 217 units
// disagreed — and the fix every time was the same: one call, not two copies.
//
// Before this module the builder panel priced the quote inline and the grid
// under it priced every level through `quoteGrid`'s own calls, pinned to
// Enterprise units, RP and SCO+ECO whatever the builder said. So a Basic-unit
// quote sat above an Enterprise grid, on one screen. The grid now takes its
// rows from here too.
//
// ── WHAT IS DELIBERATELY NOT ON THE SHEET ────────────────────────────────────
// The engine's own recommendation. The coverage the agent built IS the quote;
// printing a second, different level beside it (the builder defaults to 85%
// and a no-history prospect's recommendation is the 80% benchmark) hands a
// prospect two answers on one page. The every-level table carries the
// alternatives, as numbers, without choosing among them.
//
// And no dollar TOTAL. Kevin, 2026-08-05: "We quote $/ac, never a total." The
// acreage is a rating input, not a quantity being quoted — the same rule the
// farm sheet follows.
//
// Import-free on purpose: the engines are injected (`eng`), so the checker
// loads and drives THIS source against the real 2027 rate tables.

export const BLANK_UNIT_LABEL = { OU: "Optional", BU: "Basic", EU: "Enterprise" };
export const BLANK_PLAN_LABEL = { RP: "RP", YP: "YP", HPE: "RP-HPE" };

const pos = (v) => (v != null && v !== "" && isFinite(+v) && +v > 0 ? +v : null);

// ── the one computation ──────────────────────────────────────────────────────
// cfg: { state (FIPS), county, practice, aph, cl, unit, plan, sco, eco,
//        scoPct, ecoPct, bfr, acres, price, vol }
// eng: { quoteRP, quoteArea, countyInSet }
//
// `acres` must already be RESOLVED (see resolveAcreageBasis). This function
// never invents one — an absent acreage is a refusal, not a 1-acre premium,
// which is the defect `quoteGrid` documents at its own `|| 1`.
export function blankQuoteCoverage(cfg, eng) {
  const c = cfg || {};
  const state = String(c.state || "");
  const county = String(c.county || "").toUpperCase();
  const practice = c.practice || "003";
  const A = pos(c.aph), P = pos(c.price), AC = pos(c.acres);
  const cl = +c.cl;
  const unit = c.unit || "EU";
  const plan = c.plan || "RP";
  const vol = +c.vol;
  const bfr = !!c.bfr;
  const base = { ok: false, cl, unit, plan, practice, state, county, acres: AC };

  if (!county || !eng.countyInSet(state, county))
    return { ...base, reason: (county ? county : "That county") + " isn't in the 2027 wheat rate set." };
  if (!eng.countyInSet(state, county, practice))
    return { ...base, reason: "RMA doesn't offer this practice for wheat in " + county + " County." };
  if (A == null) return { ...base, reason: "Enter an APH to quote." };
  if (P == null) return { ...base, reason: "No price election on file for this practice — enter one to quote." };
  if (AC == null) return { ...base, reason: "No acreage to price the premium on." };

  const q = eng.quoteRP({ state, county, aph: A, cl, acres: AC, unit, price: P, vol, plan, bfr, practice });
  if (!q) return { ...base, reason: Math.round(cl * 100) + "% coverage is not rated for this county and practice." };

  // SCO/ECO follow YP vs revenue: YP rates for a YP policy, RP rates for RP and RP-HPE.
  const areaPlan = plan === "YP" ? "YP" : "RP";
  // A reduced election (RMA allows 50–100% of the band) scales coverage AND
  // premium together. A missing or zero percentage is a full election, which is
  // what the slider on screen shows for it.
  const area = (endorsement, pct) => eng.quoteArea({
    state, county, endorsement, baseCl: cl, aph: A, acres: AC, price: P, vol,
    coveragePct: (+pct || 100) / 100, plan: areaPlan, bfr, practice,
  });
  const sco = c.sco ? area("SCO", c.scoPct) : null;
  const eco = c.eco ? area("ECO", c.ecoPct) : null;

  const netAc = q.netAcre + (sco ? sco.netAcre : 0) + (eco ? eco.netAcre : 0);
  const covAc = q.liabPerAc + (sco ? sco.covPerAc : 0) + (eco ? eco.covPerAc : 0);
  const grossAc = q.grossAcre + (sco ? sco.grossAcre : 0) + (eco ? eco.grossAcre : 0);
  const netTotal = q.netTotal + (sco ? sco.netTotal : 0) + (eco ? eco.netTotal : 0);
  return {
    ok: true, reason: null, cl, unit, plan, areaPlan, practice, state, county, acres: AC,
    aph: A, price: P, vol, bfr,
    q, sco, eco,
    // An endorsement that was elected and could not be priced is NOT the same as
    // one that was not elected. Named, so a surface can say which.
    scoMissing: !!c.sco && !sco, ecoMissing: !!c.eco && !eco,
    netAc, covAc, grossAc, subsidyAc: grossAc - netAc, netTotal,
  };
}

// Every level, through the SAME function — the row at the quoted level is the
// quote, figure for figure, because it is the same call.
export function blankQuoteLevels(cfg, levels, eng) {
  return (levels || []).map((cl) => blankQuoteCoverage({ ...cfg, cl }, eng));
}

// ── the printed sheet ────────────────────────────────────────────────────────
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g,
  (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
const d2 = (n) => (n == null || !isFinite(+n) ? "&mdash;" : "$" + (Math.round(+n * 100) / 100).toFixed(2));
const pct = (x) => Math.round(+x * 100);
const acTxt = (a) => (Math.round(+a * 100) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 });

// input:
//   cfg, levels, acreage (a resolveAcreageBasis result), cropYear,
//   bands ({ scoTop, ecoBottom, ecoTop } for cropYear), logoSrc, prospect,
//   countyLabel, stateLabel, practiceLabel, priceStatus, volStatus,
//   aph: { source: "built"|"tyield"|"entered", label, tYield, build },
//   options: { bfr, mc, ql }, dateLabel
// returns { ok, html, reason } — never a page of em-dashes for a quote that
// cannot be priced.
export function blankQuotePrintHtml(input, eng) {
  const I = input || {};
  const cfg = I.cfg || {};
  const acreage = I.acreage || {};
  const bq = blankQuoteCoverage({ ...cfg, acres: acreage.acres }, eng);
  if (!bq.ok) return { ok: false, html: "", reason: bq.reason };

  const Y = I.cropYear;
  const b = I.bands || {};
  const unitLbl = BLANK_UNIT_LABEL[bq.unit] || bq.unit;
  const planLbl = BLANK_PLAN_LABEL[bq.plan] || bq.plan;
  const lvl = pct(bq.cl);
  const scoTop = pct(b.scoTop), ecoLo = pct(b.ecoBottom), ecoHi = pct(b.ecoTop);
  const measure = bq.plan === "YP" ? "yield" : "revenue";
  const areaMeasure = bq.areaPlan === "YP" ? "yield" : "revenue";
  const official = I.priceStatus === "rma";
  const priceKind = official ? "final" : I.priceStatus === "organic-manual" ? "entered" : "est.";

  // ── the acreage the premium rests on, NAMED ──
  const assumed = acreage.source === "ASSUMED";
  const acreNote = assumed ? " illustrative &mdash; none entered" : " entered";
  const acreSentence = assumed
    ? `No acreage was entered, so the premium is priced on an illustrative ${acTxt(bq.acres)} acres — an example for a prospect, not a farm's own figure. Coverage does not depend on acreage; the premium moves with it.`
    : `Premium priced on the ${acTxt(bq.acres)} acres entered.`;

  // ── where the APH came from ──
  const aph = I.aph || {};
  const aphSentence = aph.source === "built"
    ? "The approved yield was built from the yield history below. It is a Silas estimate &mdash; the AIP's approved yield governs the policy."
    : aph.source === "tyield"
      ? `The APH is the county T-yield (${esc(aph.tYield)} bu) &mdash; what RMA assigns a farm with no yield history. A grower's own records usually move it.`
      : "The APH was entered for this quote. The AIP's approved yield governs the policy.";

  // ── elections, and what each one does ──
  const o = I.options || {};
  const optCodes = [o.bfr ? "BFR/VFR" : null, o.mc ? "MC" : null, o.ql ? "QL" : null].filter(Boolean);
  const optNotes = [
    o.bfr ? "<b>BFR/VFR</b> adds 10 points of premium subsidy to every layer, SCO and ECO included" : null,
    o.mc ? "<b>MC</b> lets a small county qualify for the enterprise unit; it does not change the rate" : null,
    o.ql ? "<b>QL</b> is recorded on the quote and has no premium effect" : null,
  ].filter(Boolean);

  const title = I.prospect && String(I.prospect).trim()
    ? `${esc(String(I.prospect).trim())} &mdash; ${esc(I.countyLabel)} County, ${esc(I.stateLabel)}`
    : `${esc(I.countyLabel)} County, ${esc(I.stateLabel)}`;

  const meta = `${acTxt(bq.acres)} ac${acreNote} &middot; ${esc(unitLbl)} unit &middot; ${esc(planLbl)} &middot; `
    + `${esc(aph.label || "APH")} ${bq.aph.toFixed(1)} bu &middot; ${esc(I.practiceLabel || bq.practice)} &middot; `
    + `price $${bq.price.toFixed(2)}/bu${official ? "" : priceKind === "entered" ? " (entered)" : " est."}`
    + ` &middot; vol ${(+bq.vol).toFixed(2)}${I.volStatus === "rma" || I.volStatus === "released" ? "" : " est."}`
    + (optCodes.length ? ` &middot; options ${esc(optCodes.join(", "))}` : "");

  // ── the quoted coverage ──
  const covers = {
    MPCI: `your farm's ${measure} &middot; 0&ndash;${lvl}%`,
    SCO: `county ${areaMeasure} &middot; ${lvl}&ndash;${scoTop}%` + (+cfg.scoPct && +cfg.scoPct < 100 ? ` &middot; ${+cfg.scoPct}% of the band` : ""),
    ECO: `county ${areaMeasure} &middot; ${ecoLo}&ndash;${ecoHi}%` + (+cfg.ecoPct && +cfg.ecoPct < 100 ? ` &middot; ${+cfg.ecoPct}% of the band` : ""),
  };
  const qRow = (lbl, cov, prem, amt, cls) => `<tr${cls ? ` class="${cls}"` : ""}><td class="lbl">${lbl}</td>`
    + `<td class="lbl2">${cov}</td><td>${prem}</td><td class="cov">${amt}</td></tr>`;
  const ppFactor = bq.q.ppFactor != null ? pct(bq.q.ppFactor) : null;
  const quotedRows = [
    qRow(`MPCI ${esc(planLbl)} ${lvl}%`, covers.MPCI, d2(bq.q.netAcre), d2(bq.q.liabPerAc)),
    bq.sco ? qRow("SCO", covers.SCO, d2(bq.sco.netAcre), d2(bq.sco.covPerAc)) : "",
    bq.eco ? qRow("ECO", covers.ECO, d2(bq.eco.netAcre), d2(bq.eco.covPerAc)) : "",
    qRow("Total", "", d2(bq.netAc), d2(bq.covAc), "tot"),
    ppFactor != null && bq.q.ppLiabPerAc != null
      ? qRow("Prevent plant", `${ppFactor}% of the MPCI guarantee`, "&mdash;", d2(bq.q.ppLiabPerAc)) : "",
  ].join("");
  const missing = [bq.scoMissing ? "SCO" : null, bq.ecoMissing ? "ECO" : null].filter(Boolean);

  const stackName = `${lvl}% ${esc(unitLbl)} ${esc(planLbl)}${bq.sco ? " + SCO" : ""}${bq.eco ? " + ECO" : ""}`;
  const kpi = (v, k) => `<td style="text-align:center;padding:1px 5px;border:0">`
    + `<div style="font-size:13.5pt;font-weight:800;color:#1f5c2e;line-height:1.05">${v}</div>`
    + `<div style="font-size:6.5pt;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#8aa08a;margin-top:1px">${k}</div></td>`;
  const recPanel = `<div class="rec">
    <div class="rt">${stackName}</div>
    <table style="width:100%;border-collapse:collapse;margin-top:3px"><tr>
      ${kpi(d2(bq.netAc), "you pay /ac")}
      ${kpi(d2(bq.covAc), "coverage /ac")}
      ${kpi(d2(bq.subsidyAc), "subsidy /ac")}
      ${kpi(bq.netAc > 0 ? Math.round(bq.covAc / bq.netAc) + ":1" : "&mdash;", "cover per premium $")}
      ${kpi(bq.q.guarBu.toFixed(1), "guarantee bu/ac")}
      ${kpi(d2(bq.price), `${priceKind} price /bu`)}
    </tr></table>
  </div>`;

  // ── every level, same function, same election ──
  const rows = blankQuoteLevels(cfg, I.levels, eng)
    .map((r, i) => ({ r, cl: (I.levels || [])[i] }))
    .map(({ r, cl }) => {
      const on = Math.abs(+cl - bq.cl) < 1e-9;
      const st = on ? ' style="background:#eef6ee;font-weight:800;color:#1f5c2e"' : "";
      const cell = (p, c) => `<td${st}>${p}</td><td class="cov"${st}>${c}</td>`;
      if (!r.ok) return `<tr><td class="lbl"${st}>${pct(cl)}%</td><td class="lbl2" colspan="${2 + (bq.sco ? 2 : 0) + (bq.eco ? 2 : 0) + 2}">not rated</td></tr>`;
      return `<tr><td class="lbl"${st}>${pct(cl)}%${on ? " &middot; quoted" : ""}</td>`
        + cell(d2(r.q.netAcre), d2(r.q.liabPerAc))
        + (bq.sco ? cell(d2(r.sco && r.sco.netAcre), d2(r.sco && r.sco.covPerAc)) : "")
        + (bq.eco ? cell(d2(r.eco && r.eco.netAcre), d2(r.eco && r.eco.covPerAc)) : "")
        + cell(d2(r.netAc), d2(r.covAc)) + "</tr>";
    }).join("");
  const grpHead = (t) => `<th colspan="2" style="text-align:center;border-left:1px solid #dfe9df">${t}</th>`;
  const levelTable = `
    <div class="qt sm">Every coverage level &mdash; ${esc(unitLbl)} ${esc(planLbl)}${bq.sco ? " + SCO" : ""}${bq.eco ? " + ECO" : ""}, $ per acre</div>
    <table style="font-size:9pt">
      <thead>
        <tr><th></th>${grpHead("MPCI")}${bq.sco ? grpHead("SCO") : ""}${bq.eco ? grpHead("ECO") : ""}${grpHead("Total")}</tr>
        <tr class="sub"><th style="text-align:left">Level</th>${"<th>Prem</th><th class=\"cov\">Cov</th>".repeat(2 + (bq.sco ? 1 : 0) + (bq.eco ? 1 : 0))}</tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;

  // ── the yield history, when the APH was built from one ──
  const bld = aph.source === "built" ? aph.build : null;
  const buildBlock = bld && Array.isArray(bld.rows) && bld.rows.length ? `
    <div style="margin-top:8px;font-size:8.5pt;color:#3a4a3a">
      <b>APH database</b> &middot; ${bld.rows.map((r) => `${esc(r.year)} <b>${esc(r.y)}</b>`).join(" &middot; ")}
      ${bld.options && bld.options.length ? ` &middot; options ${esc(bld.options.join(", "))}` : ""}
      ${bld.approved != null ? ` &middot; approved <b>${esc(bld.approved)} bu</b>` : ""}
      ${bld.detail ? ` <span style="color:#60735f">(${esc(bld.detail)})</span>` : ""}
    </div>` : "";

  const areaNote = (bq.sco || bq.eco) ? `<div style="margin-top:6px;font-size:8.5pt;color:#3a4a3a;line-height:1.4">
      <b>SCO and ECO are county-index coverage.</b> They pay when the county's ${areaMeasure} falls into the band, whatever happens on this farm, and they can miss a loss this farm has that the county does not. The MPCI line is the only layer triggered by this farm's own ${measure}.</div>` : "";
  const notes = `<div style="margin-top:6px;font-size:8.5pt;color:#3a4a3a;line-height:1.4">${acreSentence} ${aphSentence}${optNotes.length ? " " + optNotes.join("; ") + "." : ""}${missing.length ? ` <b>${missing.join(" and ")} could not be priced for this county and is not included.</b>` : ""}</div>`;

  const html = `
      <section class="page">
        <div class="brand"><img class="logo" src="${esc(I.logoSrc)}" alt="Clover Ag Risk Management" /><div class="bs">Risk Management &middot; Spartan Insurance Agency LLC</div></div>
        <div class="qt">${esc(Y)} Wheat Coverage Estimate</div>
        <div class="farm">${title}</div>
        <div class="meta">${meta}</div>
        <table>
          <thead>
            <tr><th></th><th></th><th colspan="2" class="rec-h">Quoted coverage &middot; $ per acre<div class="colsub">${lvl}% ${esc(unitLbl)} ${esc(planLbl)} &middot; ${bq.q.guarBu.toFixed(1)} bu/ac @ ${d2(bq.price)}</div></th></tr>
            <tr class="sub"><th style="text-align:left">Layer</th><th class="lbl2">Covers</th><th>Premium you pay</th><th class="cov">Coverage</th></tr>
          </thead>
          <tbody>${quotedRows}</tbody>
        </table>
        ${recPanel}
        ${levelTable}
        ${buildBlock}
        ${areaNote}
        ${notes}
        <div class="disc">Estimate, not a bind &middot; producer $/ac on RMA ${esc(Y)} rates${official ? "" : ", price final after Sep 14"}${I.priceStatus === "organic-manual" ? " &middot; organic election entered by hand" : ""} &middot; Clover Ag / Spartan Insurance Agency &middot; (585) 233-1066 &middot; office@cloverag.com &middot; ${esc(I.dateLabel)}</div>
      </section>`;
  return { ok: true, html, reason: null, quote: bq };
}
